import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

const PRIVATE_HEALTH_URL = `${import.meta.env.REACT_APP_BE_API_URL}health`;
const PUBLIC_HEALTH_URL = `${import.meta.env.REACT_APP_BE_API_STATS_URL}health`;

/** Timeout per health-check request in ms */
const REQUEST_TIMEOUT_MS = 10_000;

interface NetworkError {
  /** Which endpoint failed */
  target: 'public' | 'private';
  /** Human-readable error message */
  message: string;
  /** Timestamp of the error */
  timestamp: Date;
}

export interface NetworkState {
  /** Whether the public network is reachable */
  publicConnected: boolean;
  /** Whether the private network (VPN-gated) is reachable */
  privateConnected: boolean;
  /** Whether the browser reports online status (basic connectivity) */
  isOnline: boolean;
  /** Whether the initial check is still in progress */
  isChecking: boolean;
  /** Accumulated errors from the latest check cycle */
  errors: NetworkError[];
}

interface NetworkStateContextValue extends NetworkState {
  /**
   * Run a fresh health check on demand and return the resulting state.
   * Does not flip `isChecking` — consumers showing per-route loaders won't
   * flash while a user-intent re-check (e.g. nav-menu click) is in flight.
   * Concurrent callers share a single in-flight request.
   */
  recheck: () => Promise<NetworkState>;
}

const NetworkStateContext = createContext<NetworkStateContextValue | null>(
  null,
);

/**
 * Performs a health-check fetch with a timeout.
 * Returns `true` when the endpoint responds with HTTP 200.
 */
const checkEndpoint = async (
  url: string,
  signal: AbortSignal,
): Promise<boolean> => {
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(
    () => timeoutController.abort(),
    REQUEST_TIMEOUT_MS,
  );

  const combinedSignal = AbortSignal.any([signal, timeoutController.signal]);

  try {
    const res = await fetch(url, {
      method: 'HEAD',
      cache: 'no-store',
      signal: combinedSignal,
    });
    return res.ok;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Provides shared public/private (VPN) reachability state.
 *
 * The check runs once at mount (so login / page open evaluates against fresh
 * state) and otherwise only on explicit `recheck()` calls driven by user
 * intent — e.g. a nav-menu click. There is no polling and no focus/visibility
 * listener: kiosks placed on a page are never moved by a background state
 * change. The browser `offline` event is the only passive signal we honor,
 * and it only updates state — RouteGuard ignores network changes between
 * navigations (see `RouteGuard.tsx`).
 */
export const NetworkStateProvider = ({ children }: { children: ReactNode }) => {
  const [publicConnected, setPublicConnected] = useState(false);
  const [privateConnected, setPrivateConnected] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isChecking, setIsChecking] = useState(true);
  const [errors, setErrors] = useState<NetworkError[]>([]);

  const abortRef = useRef<AbortController | null>(null);
  const inflightRef = useRef<Promise<NetworkState> | null>(null);

  const runChecks = useCallback(
    async (silent: boolean): Promise<NetworkState> => {
      // Dedupe only to a still-alive in-flight check. If the previous controller
      // was aborted (e.g. StrictMode double-mount or provider unmount), start a
      // fresh request — otherwise we'd return a dead promise and never resolve
      // `isChecking`.
      if (
        inflightRef.current &&
        abortRef.current &&
        !abortRef.current.signal.aborted
      ) {
        return inflightRef.current;
      }

      const controller = new AbortController();
      abortRef.current = controller;

      if (!silent) {
        setIsChecking(true);
      }

      const promise = (async (): Promise<NetworkState> => {
        const cycleErrors: NetworkError[] = [];

        const [publicResult, privateResult] = await Promise.allSettled([
          checkEndpoint(PUBLIC_HEALTH_URL, controller.signal),
          checkEndpoint(PRIVATE_HEALTH_URL, controller.signal),
        ]);

        const publicOk =
          publicResult.status === 'fulfilled' && publicResult.value;
        if (!publicOk) {
          cycleErrors.push({
            target: 'public',
            message:
              publicResult.status === 'rejected'
                ? publicResult.reason instanceof Error
                  ? publicResult.reason.message
                  : 'Public health check failed'
                : 'Public endpoint responded with non-OK status',
            timestamp: new Date(),
          });
        }

        const privateOk =
          privateResult.status === 'fulfilled' && privateResult.value;
        if (!privateOk) {
          cycleErrors.push({
            target: 'private',
            message:
              privateResult.status === 'rejected'
                ? privateResult.reason instanceof Error
                  ? privateResult.reason.message
                  : 'Private health check failed — VPN may be disconnected'
                : 'Private endpoint responded with non-OK status — VPN may be disconnected',
            timestamp: new Date(),
          });
        }

        const result: NetworkState = {
          publicConnected: publicOk,
          privateConnected: privateOk,
          isOnline: navigator.onLine,
          isChecking: false,
          errors: cycleErrors,
        };

        if (!controller.signal.aborted) {
          setPublicConnected(publicOk);
          setPrivateConnected(privateOk);
          setErrors(cycleErrors);
          setIsChecking(false);
        }

        return result;
      })();

      inflightRef.current = promise;
      try {
        return await promise;
      } finally {
        // Only clear if we still own the slot — a concurrent restart may have
        // already replaced it with a newer in-flight promise.
        if (inflightRef.current === promise) {
          inflightRef.current = null;
        }
      }
    },
    [],
  );

  const recheck = useCallback(() => runChecks(true), [runChecks]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => {
      setIsOnline(false);
      setPublicConnected(false);
      setPrivateConnected(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    void runChecks(false);
    return () => {
      abortRef.current?.abort();
    };
  }, [runChecks]);

  return (
    <NetworkStateContext.Provider
      value={{
        publicConnected,
        privateConnected,
        isOnline,
        isChecking,
        errors,
        recheck,
      }}
    >
      {children}
    </NetworkStateContext.Provider>
  );
};

export const useNetworkState = (): NetworkStateContextValue => {
  const ctx = useContext(NetworkStateContext);
  if (!ctx) {
    throw new Error(
      'useNetworkState must be used within a NetworkStateProvider',
    );
  }
  return ctx;
};
